import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Box, Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { WithGenericExternalActionError } from '../generic-external-action-error';

import messages from './messages';
import { RuleAction } from './rule-action';
import { StatusAction } from './status-action';
import { TransitionAction } from './transition-action';
import { UpdateStatusAction } from './UpdateStatusAction';

export const JiraWorkflowEditorUIAction = ({
	actions,
	onConfirm,
	onCancel,
	getStatus,
	conversationId,
	sendMessageConfirmedTools,
	recipientAgentId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.JiraWorkflowEditor>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const primaryAction = actions[0];
	const status = getStatus(primaryAction);
	const isDisabled = status === 'finished' || status === 'cancelled';

	const redirectOperations = primaryAction.data.operations.filter(
		(operation) => operation.operationType === 'REDIRECT_TO_WORKFLOW',
	);
	const workflowOperations = primaryAction.data.operations.filter(
		(operation) => operation.operationType !== 'REDIRECT_TO_WORKFLOW',
	);

	const { sendAnalyticsEvent } = useAnalytics();

	const onConfirmAction = useCallback(() => {
		publish({
			type: 'jira-workflow-wizard-actions',
			source: 'ai-mate',
			data: {
				invocationId: primaryAction.invocationId,
				operations: primaryAction.data.operations,
			},
		});

		primaryAction.data.operations.forEach((operation) => {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'applied',
				actionSubject: 'jiraWorkflowBuilderAction',
				attributes: {
					invocationId: primaryAction.invocationId,
					operationType: operation.operationType,
					...(operation.operationType === 'ADD_RULE'
						? { ruleTemplateKey: operation.payload.ruleTemplateKey }
						: {}),
				},
			});
		});

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'jiraWorkflowEditorRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === primaryAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
		} else {
			onConfirm(primaryAction);
		}
	}, [
		actions,
		conversationId,
		getStatus,
		messageId,
		onConfirm,
		primaryAction,
		publish,
		recipientAgentId,
		sendAnalyticsEvent,
		sendMessageConfirmedTools,
	]);

	const onCancelAction = useCallback(() => {
		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'jiraWorkflowEditorRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
		} else {
			onCancel(primaryAction);
		}
	}, [
		actions,
		conversationId,
		getStatus,
		messageId,
		onCancel,
		primaryAction,
		recipientAgentId,
		sendMessageConfirmedTools,
	]);

	return (
		<>
			{redirectOperations.length > 0 && workflowOperations.length === 0 && (
				<Box>
					{redirectOperations.map((operation) => (
						<LinkButton
							shouldFitContainer={false}
							href={addRovoParamsToUrl(operation.payload.url, {
								conversationId: conversationId || operation.payload.conversationId,
								pathway: 'chat',
							})}
						>
							{formatMessage(messages.redirectToWorkflow)}
						</LinkButton>
					))}
				</Box>
			)}
			{workflowOperations.length > 0 && (
				<WithGenericExternalActionError invocationId={primaryAction.invocationId}>
					<ActionCardWrapper
						title={formatMessage(messages.jiraWorkflowEditorUIActionTitle)}
						product="jira"
					>
						<Stack space="space.100">
							{workflowOperations.map((operation) => {
								switch (operation.operationType) {
									case 'ADD_STATUS':
									case 'DELETE_STATUS':
										return <StatusAction {...operation.payload} />;
									case 'UPDATE_STATUS':
										if (expVal('sprt_hix_9681_workflow_agent_edit_status', 'isEnabled', false)) {
											return <UpdateStatusAction {...operation.payload} />;
										}
										return null;
									case 'ADD_TRANSITION':
									case 'UPDATE_TRANSITION':
									case 'DELETE_TRANSITION':
										if (
											operation.operationType === 'DELETE_TRANSITION' &&
											!fg('hix-7888_-_delete_transition_expanded_fields')
										) {
											return null;
										}
										// @ts-expect-error - AddNew/Update/DeleteTransitionRovoPayload union not narrowed at call site; discriminated union needed
										return <TransitionAction {...operation.payload} />;
									case 'ADD_RULE':
									case 'DELETE_RULE':
										if (
											operation.operationType === 'DELETE_RULE' &&
											!fg('hix-7888_-_delete_transition_expanded_fields')
										) {
											return null;
										}
										return <RuleAction {...operation.payload} />;
									default:
										return null;
								}
							})}
							<ActionCardFooter>
								<Inline space="space.150">
									<Button appearance="primary" onClick={onConfirmAction} isDisabled={isDisabled}>
										{formatMessage(messages.applyToWorkflow)}
									</Button>
									<Button onClick={onCancelAction} isDisabled={isDisabled}>
										{formatMessage(messages.cancelAction)}
									</Button>
								</Inline>
							</ActionCardFooter>
						</Stack>
					</ActionCardWrapper>
				</WithGenericExternalActionError>
			)}
		</>
	);
};
